/**
 * AbilityPopup.js
 */

import {
    Game,
    GameTween,
    CoreEvents,
    ButtonsEvents,
    GameWindow,
    UiButton,
    UiElement,
    UiImage,
    UiText,
    WindowLayers,
    KeyboardKey,
    TINY_INITIAL_SCALE_FACTOR,
    MODAL_OPEN_CLOSE_TIME,
    MODAL_OPEN_CURVE,
    MODAL_CLOSE_CURVE,
    CloseEventSource,
    GameTimer
} from "pixi_core";

import { Hero } from "../Heroes/Hero";
import { Common } from "../game/Common";

import { GameEvents, SHORT_DELAY, HERO_ABILITY_IMAGES, ABILITY_POPUP_Y } from "../Constants";

export class AbilityPopup extends GameWindow {
    constructor() {
        super({
            name: "AbilityPopup",
            disableInputUnder: false,
            showDarkenUnder: false,
            ignoreOutsideZoneTap: false,
            layer: WindowLayers.eUNDER_GEAR
        });

        /** @private @type {UiElement} */
        this._ui = null;

        /** @private @type {UiButton} */
        this._btnClose = null;

        /** @private @type {UiImage} - Descriptive image of the ability */
        this._imgDesc = null;

        /** @private @type {UiImage} */
        this._iconAbility = null;

        /** @private @type {UiText} */
        this._lblDesc = null;

        /** @private @type {Hero} */
        this._hero = null;

        /** @private @type {Number} */
        this._positionIndex = 0;

        /** @private @type {Boolean} */
        this._reOpenAfterClose = false;

        /** @private @type {GameTween} */
        this._tweenOpen = null;
        /** @private @type {GameTween} */
        this._tweenClose = null;
        /** @private @type {GameTimer} */
        this._timerReOpenDelay = null;

        Game.on(GameEvents.eOPEN_ABILITY_POPUP, this._onOpenAbilityPopup, this);
        Game.on(CoreEvents.eINPUT_KEY_DOWN, this._onKeyDown, this);
    }

    /**
     * Custom open transition
     * @protected
     */
    _openTransition(completionCallback) {
        this._ui.scale = TINY_INITIAL_SCALE_FACTOR;
        this._ui.visible = true;

        this._tweenOpen = this.createTween(this._ui, true)
            .to({ scale: 1 }, MODAL_OPEN_CLOSE_TIME * 1000)
            .easing(MODAL_OPEN_CURVE)
            .onComplete(() => {
                if (this._tweenOpen) {
                    this._tweenOpen.destroy();
                    this._tweenOpen = null;
                }

                completionCallback();
            })
            .start();
    }

    /**
     * Custom close transition
     * @protected
     */
    _closeTransition(completionCallback) {
        this._tweenClose = this.createTween(this._ui, true)
            .to({ scale: TINY_INITIAL_SCALE_FACTOR }, MODAL_OPEN_CLOSE_TIME * 1000)
            .easing(MODAL_CLOSE_CURVE)
            .onComplete(() => {
                if (this._tweenClose) {
                    this._tweenClose.destroy();
                    this._tweenClose = null;
                }

                completionCallback();
            })
            .start();
    }

    /**
     * @private
     * @param {Hero} hero
     * @param {Number} positionIndex
     */
    _onOpenAbilityPopup(hero, positionIndex) {
        const sameIndex = this._positionIndex === positionIndex;
        this._hero = hero;
        this._positionIndex = positionIndex;

        if (this.isOpened) {
            this._reOpenAfterClose = !sameIndex;
            this.close({ instantClose: this._reOpenAfterClose });
        } else {
            this.open();
        }
    }

    /**
     * @private
     */
    _updateContentAndPosition() {
        this._imgDesc.setSprite(HERO_ABILITY_IMAGES[this._hero.name]);
        this._lblDesc.text = Common.heroes.getAbilityDescription(this._hero.name);

        this._ui.y = ABILITY_POPUP_Y[this._positionIndex];
    }

    /**
     * The window is opening for the first time
     * @protected
     */
    _onFirstWindowOpening() {
        this._ui = Game.ui.instantiate("AbilityPopup", this);

        this._imgDesc = this._ui.findElementByName("ImgDesc");
        this._lblDesc = this._ui.findElementByName("LblDesc");
        this._btnClose = this._ui.findElementByName("BtnClose");
        this._lblDesc.setLocalized(false);

        this._btnClose.setCallback(ButtonsEvents.eTAP, this._onBtnClose.bind(this));

        this.resetNavigationButtonGroups();
        this.setDefaultNavigationButton(this._btnClose);
    }

    /**
     * The window is opening
     * @protected
     * @param {Object} options
     */
    _onWindowOpening(options) {
        Game.pause("AbilityPopup");
        Game.audio.playSfx("IDSND_POPUP_OPEN");
        this._updateContentAndPosition();
    }

    /**
     * The window is opened
     * @protected
     */
    _onWindowOpened() {}

    /**
     * The window is closing
     * @protected
     */
    _onWindowClosing(options) {
        if (options && !options.instantClose) {
            Game.audio.playSfx("IDSND_POPUP_CLOSE");
        }
    }

    /**
     * The window is closed
     * @protected
     * @param {Object} event
     */
    _onWindowClosed(event) {
        Game.resume("AbilityPopup");

        if (
            event &&
            (event.closeSource === CloseEventSource.eGEAR_BUTTON ||
                event.closeSource === CloseEventSource.eANDROID_BACK_BUTTON ||
                event.closeSource === CloseEventSource.eCONTEXT_CHANGE)
        ) {
            this._reOpenAfterClose = false;

            if (this._timerReOpenDelay) {
                this._timerReOpenDelay.destroy();
                this._timerReOpenDelay = null;
            }
        }

        if (this._reOpenAfterClose) {
            this._reOpenAfterClose = false;

            if (this._timerReOpenDelay) {
                this._timerReOpenDelay.destroy();
                this._timerReOpenDelay = null;
            }

            this._timerReOpenDelay = Game.windows.getCurrentWindow().createTimer(SHORT_DELAY);
            this._timerReOpenDelay.onComplete = () => {
                this.open();
            };
        } else {
            Game.fire(GameEvents.eGAME_ENABLE_INPUTS);
        }

        if (this._tweenOpen) {
            this._tweenOpen.destroy();
            this._tweenOpen = null;
        }

        if (this._tweenClose) {
            this._tweenClose.destroy();
            this._tweenClose = null;
        }
    }

    /**
     * @private
     * @param {String} key
     */
    _onKeyDown(key) {
        if (key === KeyboardKey.eKEY_SPACE) {
            if (this.isOpened) {
                this.close();
            }
        }
    }

    /**
     * The window is closed
     * @private
     */
    _onBtnClose() {
        this.close();
    }

    /**
     * @public
     */
    navigationEscape() {
        this.close();
    }
}
